import { reactSelectInlineError } from '@nile/banyan/src/Utils/style';
import Tippy from '@tippyjs/react';
import _ from 'lodash';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import Select from 'react-select';
import { usePagination, useTable } from 'react-table';
import styled from 'styled-components';
import 'tippy.js/dist/tippy.css'; // optional
import {
  Popover,
  TextField,
  Heading,
  TextStyle,
  DisplayText
} from '@superops/ui-design-system';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  getTranslatedText,
  HeaderTag,
  T2,
  DropdownIndicator
} from '@nile/components';

const EXECUTION_TIME = [
  {
    name: 'BUSINESS_HOURS',
    label: 'Business hours',
    value: 'Business hours'
  },
  {
    name: 'CALENDAR_HOURS',
    label: 'Calendar hours 24*7',
    value: 'Calendar hours 24*7'
  }
];

const minuteList = [
  {
    unit: 'MINUTE',
    quantity: '0',
    label: '0 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '5',
    label: '5 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '10',
    label: '10 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '15',
    label: '15 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '20',
    label: '20 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '25',
    label: '25 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '30',
    label: '30 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '35',
    label: '35 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '40',
    label: '40 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '45',
    label: '45 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '50',
    label: '50 mins'
  },
  {
    unit: 'MINUTE',
    quantity: '55',
    label: '55 mins'
  }
];

const hourList = [
  {
    unit: 'HOUR',
    quantity: '0',
    label: '0 hr'
  },
  {
    unit: 'HOUR',
    quantity: '1',
    label: '1 hr'
  },
  {
    unit: 'HOUR',
    quantity: '2',
    label: '2 hr'
  },
  {
    unit: 'HOUR',
    quantity: '3',
    label: '3 hr'
  },
  {
    unit: 'HOUR',
    quantity: '4',
    label: '4 hr'
  },
  {
    unit: 'HOUR',
    quantity: '5',
    label: '5 hr'
  },
  {
    unit: 'HOUR',
    quantity: '6',
    label: '6 hr'
  },
  {
    unit: 'HOUR',
    quantity: '7',
    label: '7 hr'
  },
  {
    unit: 'HOUR',
    quantity: '8',
    label: '8 hr'
  },
  {
    unit: 'HOUR',
    quantity: '9',
    label: '9 hr'
  },
  {
    unit: 'HOUR',
    quantity: '10',
    label: '10 hr'
  },
  {
    unit: 'HOUR',
    quantity: '11',
    label: '11 hr'
  },
  {
    unit: 'HOUR',
    quantity: '12',
    label: '12 hr'
  },
  {
    unit: 'HOUR',
    quantity: '13',
    label: '13 hr'
  },
  {
    unit: 'HOUR',
    quantity: '14',
    label: '14 hr'
  },
  {
    unit: 'HOUR',
    quantity: '15',
    label: '15 hr'
  },
  {
    unit: 'HOUR',
    quantity: '16',
    label: '16 hr'
  },
  {
    unit: 'HOUR',
    quantity: '17',
    label: '17 hr'
  },
  {
    unit: 'HOUR',
    quantity: '18',
    label: '18 hr'
  },
  {
    unit: 'HOUR',
    quantity: '19',
    label: '19 hr'
  },
  {
    unit: 'HOUR',
    quantity: '20',
    label: '20 hr'
  },
  {
    unit: 'HOUR',
    quantity: '21',
    label: '21 hr'
  },
  {
    unit: 'HOUR',
    quantity: '22',
    label: '22 hr'
  },
  {
    unit: 'HOUR',
    quantity: '23',
    label: '23 hr'
  },
  {
    unit: 'HOUR',
    quantity: '24',
    label: '24 hr'
  }
];

function Table({ columns, data, updateMyData, skipPageReset }) {
  // For this example, we're using pagination to illustrate how to stop
  // the current page from resetting when our data changes
  // Otherwise, nothing is different here.
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      // use the skipPageReset option to disable page resetting temporarily
      autoResetPage: !skipPageReset,
      // updateMyData isn't part of the API, but
      // anything we put into these options will
      // automatically be available on the instance.
      // That way we can call this function from our
      // cell renderer!
      updateMyData
    },
    usePagination
  );

  // Render the UI for your table
  return (
    <>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup, idx) => (
            <tr {...headerGroup.getHeaderGroupProps()} className={`row-${idx}`}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function DurationComponent(props) {
  const {
    timeData,
    context,
    initialValue,
    durMap,
    setData,
    cellRef,
    index,
    id
  } = props;
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  });
  const updateParent = (data, index, id) => {
    const updatedData = _.cloneDeep(timeData);
    data = data.map((item) => {
      return {
        unit: item.unit,
        quantity: Number(item.quantity)
      };
    });
    if (Array.isArray(data)) {
      updatedData[index][id] = {
        fixedDuration: data,
        type: 'FIXED',
        relativeDuration: null
      };
    } else {
      updatedData[index][id] = {
        fixedDuration: null,
        type: 'RELATIVE',
        relativeDuration: data
      };
    }
    setData(updatedData);
    context.updateSLAPolicy(updatedData, 'slaTiming');
  };
  const getDurationValue = (duration) => {
    let str = '';
    for (let i = 0; i < duration.length; i++) {
      str = `${str}${duration[i].quantity} ${durMap[duration[i].unit]} `;
    }
    return str;
  };
  const activator = _.isEmpty(initialValue) ? (
    <div onClick={togglePopoverActive} className="flex center hidden bold">
      <span>0hr</span>
      <div className="triangle-down ml8" {...props} />
    </div>
  ) : (
    <div>
      {initialValue.type === 'FIXED' ? (
        <div onClick={togglePopoverActive} className="flex center bold">
          <span>{getDurationValue(initialValue.fixedDuration)}</span>
          <div className="triangle-down ml8" {...props}></div>
        </div>
      ) : (
        <div onClick={togglePopoverActive}>{initialValue.relativeDuration}</div>
      )}
    </div>
  );
  return (
    <Popover
      activator={activator}
      active={popoverActive}
      onClose={() => {
        updateParent(cellRef.current.duration, index, id);
      }}
    >
      <DurationSelector
        initialValue={
          _.isEmpty(initialValue)
            ? null
            : initialValue.type === 'FIXED'
            ? initialValue.fixedDuration
            : initialValue.relativeDuration
        }
        id={id}
        index={index}
        durRef={cellRef}
      />
    </Popover>
  );
}

const DurationSelector = (props) => {
  const { index, id, initialValue, durRef } = props;
  const [duration, setDuration] = useState(
    initialValue || [
      {
        unit: 'HOUR',
        label: '0 hr',
        quantity: '0'
      },
      {
        unit: 'MINUTE',
        label: '0 min',
        quantity: '0'
      }
    ]
  );
  const [activeInput, setActiveInput] = useState(0);
  const input1 = useRef(null);
  const input2 = useRef(null);

  // useImperativeHandle(ref, () => ({
  //   testMethod(){
  //     console.log('test method')
  //   }
  // }))

  useEffect(() => {
    if (durRef && durRef.current) {
      durRef.current = { duration };
    }
  }, [duration]);

  // return (
  //   <div>
  //     <div>
  //       <Input
  //         onClick={() => setActiveInput(0)}
  //         ref={input1}
  //         placeholder="choose value"
  //         value={`${duration[0].quantity} hrs`}
  //       />
  //       <Input
  //         onClick={() => setActiveInput(1)}
  //         ref={input2}
  //         placeholder="choose value"
  //         value={`${duration[1].quantity} mins`}
  //       />
  //     </div>
  //     <TimeList title="Duration">{renderTimeList()}</TimeList>
  //   </div>
  // );
  return (
    <div className="flex center p12">
      <div className="time hour mr-20 fx fcol w-60">
        <TextField
          autoFocus
          onChange={(val) => {
            const dur = duration.slice();
            dur[0] = {
              unit: 'HOUR',
              quantity: val
            };
            setDuration(dur);
          }}
          type="number"
          value={`${duration[0].quantity}`}
          min={0}
        />
        <div className="pl5 mt-5">
          <DisplayText size="medium">
            <TextStyle variation="subdued" weight="Medium">
              HOURS
            </TextStyle>
          </DisplayText>
        </div>
      </div>
      <div className="time minute fx fcol w-60">
        <TextField
          onChange={(val) => {
            const dur = duration.slice();
            dur[1] = {
              unit: 'MINUTE',
              quantity: val
            };
            setDuration(dur);
          }}
          type="number"
          value={`${duration[1].quantity}`}
          min={0}
          max={59}
        />
        <div className="pl5 mt-5">
          <DisplayText size="medium">
            <TextStyle variation="subdued" weight="Medium">
              MINS
            </TextStyle>
          </DisplayText>
        </div>
      </div>
    </div>
  );
};

function EscalationTimeList(props) {
  const { intl, context } = props;

  const ESCALATION_TABLE_FORM = {
    cols: [
      {
        name: 'priorityOption',
        label: ' ',
        children: [
          {
            name: 'priority',
            label: getTranslatedText(intl, 'admin.slaPolicy.table.priority'),
            type: 'text',
            accessor: 'priorityOption'
          }
        ]
      },
      {
        name: 'first_response',
        label: getTranslatedText(intl, 'admin.slaPolicy.table.firstresponse'),
        children: [
          {
            name: 'firstResponseTarget',
            label: getTranslatedText(intl, 'admin.slaPolicy.table.target'),
            type: 'popover'
          },
          {
            name: 'firstResponseEscalation',
            label: getTranslatedText(intl, 'admin.slaPolicy.table.escalate'),
            type: 'popover'
          }
        ]
      },
      {
        name: 'resolution',
        label: getTranslatedText(intl, 'admin.slaPolicy.table.resolution'),
        children: [
          {
            name: 'resolutionTarget',
            label: getTranslatedText(intl, 'admin.slaPolicy.table.target'),
            type: 'popover'
          },
          {
            name: 'resolutionEscalation',
            label: getTranslatedText(intl, 'admin.slaPolicy.table.escalate'),
            type: 'popover'
          }
        ]
      },
      {
        name: 'execution',
        label: ' ',
        children: [
          {
            name: 'executeIn',
            label: getTranslatedText(intl, 'admin.slaPolicy.table.executein'),
            type: 'dropdown',
            values: EXECUTION_TIME
          }
        ]
      }
    ]
  };
  const Styles = styled.div`
    border: 1px solid rgba(0, 0, 0, 0.05);

    table {
      border-spacing: 0;
      tr {
        height: 40px;
      }
      tr {
        :last-child {
          td {
            border-bottom: 0;
          }
        }
      }
      th:nth-child(odd) {
        border-right: 1px solid rgba(0, 0, 0, 0.05);
      }

      td:nth-child(odd) {
        border-right: 1px solid rgba(0, 0, 0, 0.05);
      }

      thead {
        box-shadow: none;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        background: #fff;
        tr {
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }
      }
      .value-selector__control {
        border-bottom: none !important;
      }
      .row-1 {
        background: #f2f2f7;
      }
      .row-1 th {
        font-size: 12px;
        text-transform: uppercase;
      }
      th,
      td {
        margin: 0;
        padding: 5px 15px;
        box-shadow: none;
        text-transform: none;
        :last-child {
          border-right: 0;
        }
      }
    }

    .pagination {
      padding: 0.5rem;
    }
  `;
  const indicatorSeparatorStyle = {
    width: 0
  };

  const IndicatorSeparator = ({ innerProps }) => {
    return <span style={indicatorSeparatorStyle} {...innerProps} />;
  };

  const durMap = {
    HOUR: 'hrs',
    MINUTE: 'mins'
  };

  const getDynamicCell = (rowObject, childColumn, cellRef) => {
    const {
      value: initialValue,
      row: { index },
      column: { id } // This is a custom function that we supplied to our table instance
    } = rowObject;

    switch (childColumn.type) {
      case 'text':
        const { priorityFields } = props;
        const val =
          priorityFields &&
          priorityFields.find((el) => el.id === initialValue?.id);
        return (
          <div className="flex center">
            <div
              className="indicator"
              style={{ background: val?.details?.colorCode || '#ececec' }}
            ></div>
            {val?.value}
          </div>
        );
      case 'dropdown':
        const handleExecutionTime = (val) => {
          let updatedDataset = _.cloneDeep(timeData);
          updatedDataset[index][id] = val?.name;
          setData(updatedDataset);
          context.updateSLAPolicy(updatedDataset, 'slaTiming');
        };
        const executeIn = timeData?.[index]?.executeIn;
        const value = executeIn
          ? childColumn.values.find((el) => {
              return el.name === executeIn || el?.value === executeIn;
            })
          : null;
        return (
          /**
           * set portal target to body so that the dropdown does not go inside the parent container
           */
          <Select
            className={``}
            classNamePrefix="value_selector"
            value={value}
            components={{ IndicatorSeparator, DropdownIndicator }}
            //title={title}
            onChange={(value) => handleExecutionTime(value)}
            options={childColumn.values}
            placeholder={childColumn.label}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            name="executionTimeSelector"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 })
            }}
          />
        );
      case 'popover':
        return (
          <DurationComponent
            timeData={timeData}
            context={context}
            initialValue={initialValue}
            durMap={durMap}
            setData={setData}
            cellRef={cellRef}
            index={index}
            id={id}
          />
        );

      default:
        return <div>Hi Vignesh!!</div>;
    }
  };

  const columns = React.useMemo(() => {
    return ESCALATION_TABLE_FORM.cols.map((column) => {
      return {
        Header: column.label,
        columns: column.children.map((childColumn) => {
          return {
            Header: childColumn.label,
            accessor: childColumn?.accessor || childColumn.name,
            Cell: (row) => {
              const cellRef = useRef({});
              return getDynamicCell(row, childColumn, cellRef);
            }
          };
        })
      };
    });
  });

  const { priorityFields } = props;
  const durationRef = useRef();
  const [durationMap, setDurationMap] = useState({});
  const t_data = context.value?.slaTiming?.length
    ? priorityFields?.map((item) => {
        let existingItem = context.value.slaTiming.find(
          (el) => el.priorityOption?.id === item?.id
        );
        if (existingItem) return existingItem;
        else {
          return {
            priorityOption: item,
            executeIn: 'BUSINESS_HOURS',
            firstResponseTarget: null,
            firstResponseEscalation: null,
            resolutionTarget: null,
            resolutionEscalation: null
          };
        }
      })
    : priorityFields.map((field) => {
        return {
          priorityOption: field,
          executeIn: 'BUSINESS_HOURS',
          firstResponseTarget: null,
          firstResponseEscalation: null,
          resolutionTarget: null,
          resolutionEscalation: null
        };
      });
  const [timeData, setData] = useState(t_data);

  return (
    <div className="fx fcol">
      <div className="flex mb-10">
        <Heading element="h3" weight="SemiBold">
          <FormattedMessage id="admin.slaPolicy.h2.targetEscalation" />
        </Heading>
      </div>
      {props.errors?.slaTiming &&
        reactSelectInlineError(props.errors.slaTiming[0])}
      <Styles name="Esclations">
        <Table columns={columns} data={timeData} />
      </Styles>
    </div>
  );
}

export default injectIntl(EscalationTimeList);
